import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { resolveImageUrl, NEWS_PLACEHOLDER } from '../utils/imageHelper';

const CATEGORIES = ['All', 'News', 'Events', 'Announcements', 'Success Stories', 'Community'];

const NewsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { error } = useToast();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);

        // ✅ Correct route — your backend uses /posts not /news/posts
        const { data } = await api.get('/posts');

        // Handle both array and object responses
        let postsArray = [];
        if (Array.isArray(data)) {
          postsArray = data;
        } else if (data && Array.isArray(data.posts)) {
          postsArray = data.posts;
        } else if (data && Array.isArray(data.data)) {
          postsArray = data.data;
        }

        // Sort by newest first
        postsArray.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setPosts(postsArray);
      } catch (err) {
        console.error('Failed to fetch posts:', err);
        error('Failed to load news. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [error]);

  // Filter by category
  const filteredPosts = selectedCategory === 'All'
    ? posts
    : posts.filter(p => p.category === selectedCategory);

  const featuredPost = selectedCategory === 'All' && posts.length > 0
    ? posts[0]
    : null;

  const gridPosts = featuredPost
    ? filteredPosts.slice(1)
    : filteredPosts;

  // Safely get post image from any field
  const getPostImage = (post) => {
    const raw = post?.images?.[0] || post?.imageUrl || post?.image || null;
    return resolveImageUrl(raw, NEWS_PLACEHOLDER);
  };

  // Format Firestore timestamp or string date
  const formatDate = (post) => {
    try {
      if (post.createdAt?.seconds) {
        return new Date(post.createdAt.seconds * 1000).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'short', year: 'numeric',
        });
      }
      if (post.createdAt?.toDate) {
        return post.createdAt.toDate().toLocaleDateString('en-IN');
      }
      if (typeof post.createdAt === 'string') {
        return new Date(post.createdAt).toLocaleDateString('en-IN');
      }
    } catch (e) {}
    return 'Recent';
  };

  // Strip HTML tags from content
  const stripHtml = (str, limit = 120) => {
    if (!str) return '';
    return str.replace(/<[^>]*>/g, '').substring(0, limit) + '...';
  };

  return (
    <div className="news-page">
      {/* Hero */}
      <section className="news-hero">
        <div className="container">
          <span className="news-hero__eyebrow">Community Updates</span>
          <h1>📰 News & Updates</h1>
          <p>Stay connected with the latest happenings of Kumbhar Samaj</p>
        </div>
      </section>

      <div className="container news-container">
        {/* Featured Post — only when All tab is active */}
        {!loading && featuredPost && (
          <Link
            to={`/post/${featuredPost.id}`}
            className="featured-post"
          >
            <div className="featured-post__image">
              <img
                src={getPostImage(featuredPost)}
                alt={featuredPost.title}
                onError={(e) => { e.target.src = NEWS_PLACEHOLDER; }}
              />
            </div>
            <div className="featured-post__content">
              <div className="post-meta">
                <span className="cat-pill">{featuredPost.category || 'News'}</span>
                <span className="post-date">📅 {formatDate(featuredPost)}</span>
                <span className="featured-label">⭐ Featured</span>
              </div>
              <h2>{featuredPost.title}</h2>
              <p>{stripHtml(featuredPost.content, 200)}</p>
              <span className="read-more-btn">Read Full Article →</span>
            </div>
          </Link>
        )}

        {/* Category Tabs */}
        <div className="cat-tabs">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`cat-tab ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
              {cat !== 'All' && posts.filter(p => p.category === cat).length > 0 && (
                <span className="cat-count">
                  {posts.filter(p => p.category === cat).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Results count */}
        {!loading && (
          <div className="results-info">
            {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''} found
            {selectedCategory !== 'All' && ` in "${selectedCategory}"`}
          </div>
        )}

        {/* Posts Grid */}
        {loading ? (
          <div className="posts-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : gridPosts.length > 0 ? (
          <div className="posts-grid">
            {gridPosts.map((post) => (
              <Link
                key={post.id}
                to={`/post/${post.id}`}
                className="post-card"
              >
                <div className="post-card__img">
                  <img
                    src={getPostImage(post)}
                    alt={post.title}
                    onError={(e) => { e.target.src = NEWS_PLACEHOLDER; }}
                  />
                </div>
                <div className="post-card__body">
                  <div className="post-meta">
                    <span className="cat-pill">{post.category || 'News'}</span>
                    <span className="post-date">{formatDate(post)}</span>
                  </div>
                  <h3>{post.title}</h3>
                  <p>{stripHtml(post.content, 100)}</p>
                  <span className="post-read-more">Read More →</span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <span>📰</span>
            <h3>
              {selectedCategory !== 'All'
                ? `No posts in "${selectedCategory}" yet`
                : 'No news posts yet'}
            </h3>
            <p>
              {selectedCategory !== 'All'
                ? 'Try another category or check back later.'
                : 'Check back soon for community updates.'}
            </p>
            {selectedCategory !== 'All' && (
              <button
                className="btn btn-primary"
                onClick={() => setSelectedCategory('All')}
              >
                View All Posts
              </button>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .news-page {
          min-height: 100vh;
          background: var(--neutral-50);
          padding-bottom: var(--space-16);
        }

        /* Hero */
        .news-hero {
          background: linear-gradient(135deg, var(--primary-800), var(--primary-600));
          padding: var(--space-16) 0;
          text-align: center;
          margin-bottom: var(--space-10);
        }
        .news-hero__eyebrow {
          display: inline-block;
          background: rgba(255,255,255,0.15);
          color: rgba(255,255,255,0.9);
          font-size: var(--text-xs);
          font-weight: var(--weight-bold);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          padding: var(--space-1) var(--space-4);
          border-radius: var(--radius-full);
          margin-bottom: var(--space-3);
        }
        .news-hero h1 {
          font-size: var(--text-5xl);
          color: white;
          margin-bottom: var(--space-3);
        }
        .news-hero p {
          color: rgba(255,255,255,0.85);
          font-size: var(--text-lg);
          margin: 0;
        }

        .news-container {
          max-width: 1200px;
        }

        /* Featured Post */
        .featured-post {
          display: grid;
          grid-template-columns: 1.3fr 1fr;
          background: white;
          border-radius: var(--radius-2xl);
          overflow: hidden;
          box-shadow: var(--shadow-lg);
          margin-bottom: var(--space-8);
          text-decoration: none;
          border: 1px solid var(--neutral-200);
          transition: all var(--transition-base);
        }
        .featured-post:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-xl);
        }
        .featured-post__image {
          position: relative;
          overflow: hidden;
          min-height: 300px;
        }
        .featured-post__image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--transition-slow);
        }
        .featured-post:hover .featured-post__image img {
          transform: scale(1.04);
        }
        .featured-post__content {
          padding: var(--space-8);
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: var(--space-4);
        }
        .featured-post__content h2 {
          font-size: var(--text-2xl);
          color: var(--primary-900);
          margin: 0;
          line-height: var(--leading-snug);
        }
        .featured-post__content p {
          color: var(--neutral-600);
          font-size: var(--text-base);
          line-height: var(--leading-relaxed);
          margin: 0;
        }
        .read-more-btn {
          color: var(--primary-500);
          font-weight: var(--weight-semibold);
          font-size: var(--text-sm);
        }

        /* Post Meta */
        .post-meta {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          flex-wrap: wrap;
        }
        .cat-pill {
          font-size: 10px;
          font-weight: var(--weight-bold);
          color: var(--primary-600);
          background: var(--primary-50);
          padding: 2px var(--space-3);
          border-radius: var(--radius-full);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 1px solid var(--primary-100);
        }
        .post-date {
          font-size: var(--text-xs);
          color: var(--neutral-500);
        }
        .featured-label {
          font-size: var(--text-xs);
          font-weight: var(--weight-bold);
          color: var(--gold-700);
          background: var(--gold-100);
          padding: 2px var(--space-2);
          border-radius: var(--radius-full);
          border: 1px solid var(--gold-300);
        }

        /* Category Tabs */
        .cat-tabs {
          display: flex;
          gap: var(--space-2);
          overflow-x: auto;
          padding: var(--space-1) 0 var(--space-4);
          margin-bottom: var(--space-2);
          scrollbar-width: none;
        }
        .cat-tabs::-webkit-scrollbar { display: none; }

        .cat-tab {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-2) var(--space-5);
          border: 2px solid var(--neutral-200);
          background: white;
          border-radius: var(--radius-full);
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          color: var(--neutral-600);
          cursor: pointer;
          white-space: nowrap;
          transition: all var(--transition-fast);
          font-family: var(--font-body);
        }
        .cat-tab:hover {
          border-color: var(--primary-400);
          color: var(--primary-600);
          background: var(--primary-50);
        }
        .cat-tab.active {
          background: var(--primary-500);
          border-color: var(--primary-500);
          color: white;
        }
        .cat-count {
          background: rgba(255,255,255,0.25);
          padding: 0 var(--space-2);
          border-radius: var(--radius-full);
          font-size: 10px;
        }
        .cat-tab:not(.active) .cat-count {
          background: var(--neutral-200);
          color: var(--neutral-700);
        }

        /* Results info */
        .results-info {
          font-size: var(--text-sm);
          color: var(--neutral-500);
          margin-bottom: var(--space-4);
        }

        /* Posts Grid */
        .posts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: var(--space-6);
        }

        /* Post Card */
        .post-card {
          background: white;
          border-radius: var(--radius-xl);
          overflow: hidden;
          border: 1px solid var(--neutral-200);
          text-decoration: none;
          transition: all var(--transition-base);
          display: flex;
          flex-direction: column;
          box-shadow: var(--shadow-xs);
        }
        .post-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-200);
        }
        .post-card__img {
          aspect-ratio: 16/9;
          overflow: hidden;
          background: var(--neutral-100);
        }
        .post-card__img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--transition-slow);
        }
        .post-card:hover .post-card__img img {
          transform: scale(1.05);
        }
        .post-card__body {
          padding: var(--space-5);
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }
        .post-card__body h3 {
          font-size: var(--text-lg);
          font-weight: var(--weight-bold);
          color: var(--neutral-900);
          margin: 0;
          line-height: var(--leading-snug);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .post-card__body p {
          font-size: var(--text-sm);
          color: var(--neutral-600);
          line-height: var(--leading-relaxed);
          margin: 0;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .post-read-more {
          font-size: var(--text-xs);
          font-weight: var(--weight-semibold);
          color: var(--primary-500);
          margin-top: var(--space-2);
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: var(--space-20);
          grid-column: 1 / -1;
          color: var(--neutral-600);
        }
        .empty-state span {
          font-size: 4rem;
          display: block;
          margin-bottom: var(--space-4);
        }
        .empty-state h3 {
          font-size: var(--text-2xl);
          color: var(--neutral-800);
          margin-bottom: var(--space-2);
        }
        .empty-state p {
          margin-bottom: var(--space-6);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .featured-post {
            grid-template-columns: 1fr;
          }
          .featured-post__image {
            min-height: 220px;
          }
          .featured-post__content {
            padding: var(--space-5);
          }
          .posts-grid {
            grid-template-columns: 1fr;
          }
          .news-hero h1 {
            font-size: var(--text-4xl);
          }
        }
      `}</style>
    </div>
  );
};

export default NewsPage;